import React from 'react';
import { ValidationErrors } from 'final-form';
import * as Yup from 'yup';
import { Button, Dialog, DialogProps, Tabs } from '@economic/taco';
import { Agreement, useCurrentAgreement } from '@economic/react-app';

import { Row, Column } from '../../shared/components/Grid/Grid';
import { Form, Field, Condition } from '../../shared/components/Form';
import { t, useLocalization } from '../../shared/components/l10n/l10n';
import AccountCombobox from '../../shared/components/ui/AccountCombobox';
import internalApiClient from '../../shared/util/api/internalApiClient';
import { FormProps } from '../types';
import { ProductGroup } from '../../../types/internal-api';
import Loader from '../../shared/components/Loader';
import useCachedQuery from '../../shared/hooks/useCachedQuery';
import { useDisableKeyShortcuts } from '../../shared/hooks/useKeyShortcut';
import { AccountModel } from '../../views/Accounts';
import CountrySpecificVatTable from './CountrySpecificVat/CountrySpecificVatTable';
import useQuery from '../../shared/hooks/useQuery';

type ProductGroupFormProps = Omit<FormProps<ProductGroup>, 'onSubmitted'> & {
    showSaveAndNew?: boolean;
    setShouldRenderNew?: (shouldRenderNew: boolean) => void;
    onSubmitted?: (response: ProductGroup, createNew?: boolean) => void;
    arrowKeySaveAndChangeHandler?: (nextItemId: string | number) => void;
    collectionData?: any[];
    idKeyProp?: string;
    currentItemId?: string | number;
};
type ProductGroupDialogProps = Partial<DialogProps> &
    Pick<
        ProductGroupFormProps,
        | 'initialData'
        | 'onSubmitted'
        | 'showSaveAndNew'
        | 'collectionData'
        | 'idKeyProp'
        | 'currentItemId'
        | 'arrowKeySaveAndChangeHandler'
    >;

enum CreateProductTab {
    General = 'tab-general',
    Inventory = 'tab-inventory',
    CountrySpecificVat1 = 'country-specific-vat1',
    CountrySpecificVat2 = 'country-specific-vat2',
}

const initialValues: Partial<ProductGroup> = {
    id: undefined,
    name: undefined,
    accrualAccountNumber: undefined,
    includeInProject: false,
    isServiceProducts: undefined, // this is set to undefined in order to force user to choose product group with or without withdrawal
    inventoryAccountNumber: undefined,
    costOfGoodsSoldAccountNumber: undefined,
    accountingRules: undefined,
};

const validationSchema = () =>
    Yup.object().shape({
        id: Yup.number()
            .required()
            .typeError(t('GENERALINPUT_ERROR_INVALIDDETGOODSGROUP', { CURRENTFIELDNAME: t`FORM_FIELD_LABEL_NUMBER` })),
        name: Yup.string().max(50).required(),
        includeInProjects: Yup.bool(),
        accrualAccountNumber: Yup.number().nullable(),
        isServiceProducts: Yup.bool()
            .nullable()
            .when('$agreement', {
                is: (agreement: Agreement) => agreement.modules.inventory === true,
                then: Yup.bool().required(),
            }),
        inventoryAccountNumber: Yup.number()
            .nullable()
            .when(['isServiceProducts', '$agreement'], {
                is: (isServiceProducts, agreement: Agreement) =>
                    isServiceProducts === false && agreement.modules.inventory === true,
                then: Yup.number().required(),
            }),
        costOfGoodsSoldAccountNumber: Yup.number()
            .nullable()
            .when(['isServiceProducts', '$agreement'], {
                is: (isServiceProducts, agreement: Agreement) =>
                    isServiceProducts === false && agreement.modules.inventory === true,
                then: Yup.number().required(),
            }),
        accountingRules: Yup.object({
            domesticAccountNumber: Yup.number().required(),
            domesticWithoutVatAccountNumber: Yup.number().nullable(),
            euAccountNumber: Yup.number().nullable(),
            abroadAccountNumber: Yup.number().nullable(),
            currentProjectsAccountNumber: Yup.number().nullable(),
        }),
    });

function ProductGroupForm({
    initialData = initialValues,
    notifications,
    onCancel,
    onSubmitted,
    showSaveAndNew,
    arrowKeySaveAndChangeHandler,
    collectionData,
    idKeyProp,
    currentItemId,
}: ProductGroupFormProps) {
    const agreement = useCurrentAgreement();
    const serviceProductDropdownData = React.useMemo(
        () => [
            { text: t`FORM_FIELD_OPTION_STOCK_CHOICE_WITHSTOCK`, value: false },
            { text: t`FORM_FIELD_OPTION_STOCK_CHOICE_WITHOUTSTOCK`, value: true },
        ],
        []
    );

    const { data: showCountrySpecificFeatures } = useQuery<boolean>('/vat/show-country-specific-vat-features', false);
    const hasProjectModule = agreement.modules.project;
    const hasAccrualModule = agreement.modules.accruals || agreement.modules.fixedAssetsAndAccruals;
    const hasInventoryModule = agreement.modules.inventory;
    const disableServiceProductsField = initialData?.isServiceProducts === false;
    const isEditing = !!initialData.id;
    const [createNew, setCreateNew] = React.useState(false);

    const handleSubmit = async (values: ProductGroup) => {
        try {
            let response;
            // When product group is created from agreement that don't have inventory module enabled
            // isServiceProducts is set to undefined (from initialValues) and it should be set to true
            const productGroupValues = values.isServiceProducts === undefined ? { ...values, isServiceProducts: true } : values;

            // If inventory withdrawal is not used, then don't send inventory withdrawal related fields.
            // This is necessary because user can set up inventory withdrawal related fields and then
            // choose to not use inventory withdrawal.
            if (productGroupValues.isServiceProducts) {
                delete productGroupValues.costOfGoodsSoldAccountNumber;
                delete productGroupValues.inventoryAccountNumber;
            }

            if (initialData.id !== undefined) {
                response = await internalApiClient.put(`/products/groups/${initialData.id}`, productGroupValues);
            } else {
                response = await internalApiClient.post(`/products/groups`, productGroupValues);
            }

            return Promise.resolve(response);
        } catch (error) {
            return Promise.reject(error);
        }
    };

    // This is a temporary fix for showing user feedback when they haven't filled the mandatory
    // inventory fields.
    const getErrorMessage = (errors: ValidationErrors) => {
        if (!errors) return undefined;

        let fieldName: string | undefined;

        if (errors.isServiceProducts) {
            fieldName = t`FORM_FIELD_LABEL_STOCKACCOUNT`;
        } else if (errors.inventoryAccountNumber) {
            fieldName = t`FORM_FIELD_LABEL_GOODSPURCHASESTOCK`;
        } else if (errors.costOfGoodsSoldAccountNumber) {
            fieldName = t`FORM_FIELD_LABEL_GOODSCONSUMPTION`;
        }

        return fieldName && t('GENERALINPUT_ERROR_FILLCURRENTFIELD', { CURRENTFIELDNAME: fieldName });
    };

    return (
        <Form
            context={{ agreement }}
            validateOnBlur={false}
            initialData={initialData}
            messages={notifications}
            onSubmit={handleSubmit}
            onSubmitted={productGroup => onSubmitted && onSubmitted(productGroup, createNew)}
            validationSchema={validationSchema}
            onArrowKeySaveAndChange={arrowKeySaveAndChangeHandler}
            collectionData={collectionData}
            idKeyProp={idKeyProp}
            currentItemId={currentItemId}>
            {({ errors, submitFailed, handleSubmit }) => (
                <>
                    <h1>{t`FORM_GOODSGROUPS_APPLETHEADLINE`}</h1>

                    <Tabs aria-label={t`PRODUCTGROUP_DIALOG_TABS`} defaultId={CreateProductTab.General}>
                        <Tabs.List>
                            <Tabs.Trigger id={CreateProductTab.General}>{t`FIREBIRD_FORM_CUSTOMERS_TAB_GENERAL`}</Tabs.Trigger>
                            {hasInventoryModule && (
                                <Tabs.Trigger id={CreateProductTab.Inventory}>{`${t`FIREBIRD_TAB_INVENTORY`} *`}</Tabs.Trigger>
                            )}
                            {showCountrySpecificFeatures && (
                                <>
                                    <Tabs.Trigger
                                        id={
                                            CreateProductTab.CountrySpecificVat1
                                        }>{t`FIREBIRD_FORM_CUSTOMERS_TAB_COUNTRY_SPECIFIC_VAT1`}</Tabs.Trigger>
                                    <Tabs.Trigger
                                        id={
                                            CreateProductTab.CountrySpecificVat2
                                        }>{t`FIREBIRD_FORM_CUSTOMERS_TAB_COUNTRY_SPECIFIC_VAT2`}</Tabs.Trigger>
                                </>
                            )}
                        </Tabs.List>
                        <Tabs.Content id={CreateProductTab.General}>
                            <Row>
                                <Column>
                                    <Field.Number
                                        autoFocus={!isEditing}
                                        label={t`FORM_FIELD_LABEL_NUMBER`}
                                        name="id"
                                        readOnly={isEditing}
                                        required
                                    />
                                    <Field.Text
                                        autoFocus={isEditing}
                                        label={t`FORM_FIELD_LABEL_NAME`}
                                        name="name"
                                        maxLength="50"
                                        required
                                    />
                                </Column>
                                <Column>
                                    <Field.Combobox
                                        as={AccountCombobox}
                                        label={t`FORM_FIELD_LABEL_VATZONE_DENMARK`}
                                        name="accountingRules.domesticAccountNumber"
                                        required
                                    />
                                    <Field.Combobox
                                        as={AccountCombobox}
                                        label={t`FORM_FIELD_LABEL_VATZONE_EU`}
                                        name="accountingRules.euAccountNumber"
                                    />
                                    <Field.Combobox
                                        as={AccountCombobox}
                                        label={t`FORM_FIELD_LABEL_VATZONE_OUTSIDEEU`}
                                        name="accountingRules.abroadAccountNumber"
                                    />
                                </Column>
                                <Column>
                                    <Field.Combobox
                                        as={AccountCombobox}
                                        label={t`FORM_FIELD_LABEL_VATZONE_DOMESTICNOVAT`}
                                        name="accountingRules.domesticWithoutVatAccountNumber"
                                    />
                                    {hasProjectModule && (
                                        <Field.Combobox
                                            as={AccountCombobox}
                                            label={t`FORM_FIELD_LABEL_VATZONE_PROJECTACCOUNT`}
                                            name="accountingRules.currentProjectsAccountNumber"
                                        />
                                    )}
                                </Column>
                                <Column>
                                    {hasAccrualModule && (
                                        <Field.Combobox
                                            as={AccountCombobox}
                                            label={t`FORM_FIELD_LABEL_ACCRUALACCOUNTS`}
                                            name="accrualAccountNumber"
                                        />
                                    )}
                                    {hasProjectModule && (
                                        <Field.Checkbox label={t`FORM_FIELD_LABEL_INCLUDEINCASES`} name="includeInProject" />
                                    )}
                                </Column>
                            </Row>
                        </Tabs.Content>
                        <Tabs.Content id={CreateProductTab.Inventory}>
                            <Row>
                                <Column xs="3">
                                    <Field.Dropdown
                                        disabled={disableServiceProductsField}
                                        label={t`FORM_FIELD_LABEL_STOCKACCOUNT`}
                                        name="isServiceProducts"
                                        emptyValue={null}
                                        options={serviceProductDropdownData}
                                        required
                                    />
                                </Column>
                                <Column xs="3">
                                    <Condition for="isServiceProducts" when={value => value === false}>
                                        <Field.Combobox
                                            as={AccountCombobox}
                                            label={t`FORM_FIELD_LABEL_GOODSPURCHASESTOCK`}
                                            name="inventoryAccountNumber"
                                            required
                                        />
                                        <Field.Combobox
                                            as={AccountCombobox}
                                            label={t`FORM_FIELD_LABEL_GOODSCONSUMPTION`}
                                            name="costOfGoodsSoldAccountNumber"
                                            required
                                        />
                                    </Condition>
                                </Column>
                            </Row>
                        </Tabs.Content>
                        <Tabs.Content id={CreateProductTab.CountrySpecificVat1}>
                            <Row>
                                <Column>
                                    <Field.Array name="accountingRules.countrySpecificVatRuleItems">
                                        {({ fields }) => {
                                            return (
                                                <CountrySpecificVatTable
                                                    fields={fields}
                                                    columnsCount={4}
                                                    startIndex={0}
                                                    endIndex={15}
                                                />
                                            );
                                        }}
                                    </Field.Array>
                                </Column>
                            </Row>
                        </Tabs.Content>
                        <Tabs.Content id={CreateProductTab.CountrySpecificVat2}>
                            <Row>
                                <Column>
                                    <Field.Array name="accountingRules.countrySpecificVatRuleItems">
                                        {({ fields }) => {
                                            return (
                                                <CountrySpecificVatTable
                                                    fields={fields}
                                                    columnsCount={4}
                                                    startIndex={16}
                                                    endIndex={31}
                                                />
                                            );
                                        }}
                                    </Field.Array>
                                </Column>
                            </Row>
                        </Tabs.Content>
                    </Tabs>
                    <div className="form__error__wrapper">
                        {submitFailed && <p className="form__error">{getErrorMessage(errors)}</p>}
                        <div className="form__buttons">
                            {showSaveAndNew && (
                                <Button
                                    type="submit"
                                    onClick={e => {
                                        // Makes sure that when user press enter, all the fields are blurred
                                        // and form values are updated before submit
                                        (e.target as any).focus();
                                        setCreateNew(true);
                                    }}
                                    appearance="primary">
                                    {t`BUTTON_SAVE_AND_NEW`}
                                </Button>
                            )}

                            <Button
                                type={showSaveAndNew ? 'button' : 'submit'}
                                appearance={showSaveAndNew ? 'default' : 'primary'}
                                onClick={e => {
                                    // Makes sure that when user press enter all the fields are blurred
                                    // and form values are updated before submit
                                    (e.target as any).focus();
                                    setCreateNew(false);
                                    handleSubmit(e);
                                }}>
                                {t`BUTTON_SAVE`}
                            </Button>
                            {onCancel && (
                                <Button onClick={onCancel} appearance="default">
                                    {t`BUTTON_CANCEL`}
                                </Button>
                            )}
                        </div>
                    </div>
                </>
            )}
        </Form>
    );
}

export default function CreateOrEditProductGroup(props: ProductGroupFormProps) {
    //  Preloads accounts and prevents every account combobox inside ProductGroupForm from triggering a query
    const accountsQuery = useCachedQuery<AccountModel[]>('/accounts', []);
    const loading = useLocalization(textIds);

    useDisableKeyShortcuts();

    return loading || !accountsQuery.__initialized ? (
        <div className="h-40">
            <Loader />
        </div>
    ) : (
        <ProductGroupForm {...props} />
    );
}

export const CreateProductGroupDialog = ({
    initialData,
    onSubmitted,
    collectionData,
    currentItemId,
    idKeyProp,
    arrowKeySaveAndChangeHandler,
    ...dialogProps
}: ProductGroupDialogProps) => {
    const [createCounter, setCreateCounter] = React.useState(1);

    const handleClose = (close: () => void) => {
        close();
        if (dialogProps.onClose) {
            dialogProps.onClose();
        }
    };

    return (
        <Dialog {...dialogProps} size="md" draggable>
            <Dialog.Content aria-label={t`FORM_GOODSGROUPS_CREATETEXT`}>
                {({ close }) => (
                    <CreateOrEditProductGroup
                        initialData={initialData}
                        showSaveAndNew
                        onCancel={() => handleClose(close)}
                        key={createCounter}
                        onSubmitted={(productGroup, createNew) => {
                            if (onSubmitted) {
                                onSubmitted(productGroup, false);
                            }
                            if (!createNew) {
                                handleClose(close);
                            } else {
                                setCreateCounter(createCounter + 1);
                            }
                        }}
                        collectionData={collectionData}
                        currentItemId={currentItemId}
                        idKeyProp={idKeyProp}
                        arrowKeySaveAndChangeHandler={arrowKeySaveAndChangeHandler}
                    />
                )}
            </Dialog.Content>
        </Dialog>
    );
};

export const EditProductGroupDialog = ({
    initialData,
    onSubmitted,
    collectionData,
    currentItemId,
    idKeyProp,
    arrowKeySaveAndChangeHandler,
    ...dialogProps
}: ProductGroupDialogProps) => {
    const handleClose = (close: () => void) => {
        close();
        if (dialogProps.onClose) {
            dialogProps.onClose();
        }
    };

    return (
        <Dialog {...dialogProps} size="md" draggable>
            <Dialog.Content aria-label={t`FORM_GOODSGROUPS_CREATETEXT`}>
                {({ close }) => (
                    <CreateOrEditProductGroup
                        initialData={initialData}
                        onCancel={() => handleClose(close)}
                        onSubmitted={productGroup => {
                            if (onSubmitted) {
                                onSubmitted(productGroup, false);
                            }
                            handleClose(close);
                        }}
                        collectionData={collectionData}
                        currentItemId={currentItemId}
                        idKeyProp={idKeyProp}
                        arrowKeySaveAndChangeHandler={arrowKeySaveAndChangeHandler}
                    />
                )}
            </Dialog.Content>
        </Dialog>
    );
};

const textIds = [
    'FORM_GOODSGROUPS_APPLETHEADLINE',
    'PRODUCTGROUP_DIALOG_TABS',
    'FIREBIRD_FORM_CUSTOMERS_TAB_GENERAL',
    'FIREBIRD_TAB_INVENTORY',
    'FIREBIRD_FORM_CUSTOMERS_TAB_COUNTRY_SPECIFIC_VAT1',
    'FIREBIRD_FORM_CUSTOMERS_TAB_COUNTRY_SPECIFIC_VAT2',
    'FORM_FIELD_LABEL_VATZONE_DENMARK',
    'FORM_FIELD_LABEL_VATZONE_EU',
    'FORM_FIELD_LABEL_VATZONE_OUTSIDEEU',
    'FORM_FIELD_LABEL_VATZONE_DOMESTICNOVAT',
    'FORM_FIELD_LABEL_VATZONE_PROJECTACCOUNT',
    'FORM_FIELD_LABEL_ACCRUALACCOUNTS',
    'FORM_FIELD_LABEL_INCLUDEINCASES',
    'FORM_FIELD_LABEL_STOCKACCOUNT',
    'FORM_FIELD_LABEL_GOODSPURCHASESTOCK',
    'FORM_FIELD_LABEL_GOODSCONSUMPTION',
    'GENERALINPUT_ERROR_INVALIDDETGOODSGROUP',
    'FORM_FIELD_OPTION_STOCK_CHOICE_WITHSTOCK',
    'FORM_FIELD_OPTION_STOCK_CHOICE_WITHOUTSTOCK',
    'GENERALINPUT_ERROR_FILLCURRENTFIELD',
    'SBA_COUNTRY_AT',
    'SBA_COUNTRY_BE',
    'SBA_COUNTRY_BG',
    'SBA_COUNTRY_CY',
    'SBA_COUNTRY_CZ',
    'SBA_COUNTRY_DE',
    'SBA_COUNTRY_EE',
    'SBA_COUNTRY_GR',
    'SBA_COUNTRY_ES',
    'SBA_COUNTRY_FI',
    'SBA_COUNTRY_FR',
    'SBA_COUNTRY_HR',
    'SBA_COUNTRY_HU',
    'SBA_COUNTRY_IE',
    'SBA_COUNTRY_IT',
    'SBA_COUNTRY_LT',
    'SBA_COUNTRY_LU',
    'SBA_COUNTRY_LV',
    'SBA_COUNTRY_MT',
    'SBA_COUNTRY_NL',
    'SBA_COUNTRY_PL',
    'SBA_COUNTRY_PT',
    'SBA_COUNTRY_RO',
    'SBA_COUNTRY_SE',
    'SBA_COUNTRY_SI',
    'SBA_COUNTRY_SK',
    'SBA_COUNTRY_XI',
    'SBA_COUNTRY_XU',
];
